import React, { useState, useMemo } from 'react';
import { Calculator } from 'lucide-react';

// Ürünün sarfiyat verisinden hesaplama yöntemini çıkarır.
// Öncelik: technicalData.sarfiyat_L_m2 / sarfiyat_kg_m2 (birim/m²).
// Yoksa: consumption/sarfiyat metnindeki "X-Y m²/L" (kapsama) veya "X L/m²" (birim/alan).
export function parseConsumptionRate(product) {
    const nums = (s) => (String(s).match(/\d+[.,]?\d*/g) || [])
        .map((x) => parseFloat(x.replace(',', '.')))
        .filter((n) => !isNaN(n) && n > 0);

    let td = null;
    try { td = product?.technicalData ? JSON.parse(product.technicalData) : null; } catch (e) { td = null; }

    if (td && td.sarfiyat_L_m2) {
        const n = nums(td.sarfiyat_L_m2);
        if (n.length) return { mode: 'per_area', unit: 'L', min: Math.min(...n), max: Math.max(...n), label: `${td.sarfiyat_L_m2} L/m²` };
    }
    if (td && td.sarfiyat_kg_m2) {
        const n = nums(td.sarfiyat_kg_m2);
        if (n.length) return { mode: 'per_area', unit: 'kg', min: Math.min(...n), max: Math.max(...n), label: `${td.sarfiyat_kg_m2} kg/m²` };
    }

    const text = product?.consumption || product?.sarfiyat || '';
    // Kapsama: "15-18 m²/L" (m² per litre/kg)
    let m = text.match(/([\d.,]+)\s*[-–—]?\s*([\d.,]+)?\s*m²\s*\/\s*(lt|litre|l|kg)\b/i);
    if (m) {
        const n = [m[1], m[2]].filter(Boolean).map((x) => parseFloat(x.replace(',', '.'))).filter((v) => v > 0);
        if (n.length) return { mode: 'coverage', unit: /kg/i.test(m[3]) ? 'kg' : 'L', min: Math.min(...n), max: Math.max(...n), label: m[0].trim() };
    }
    // Birim/alan: "0.055 L/m²" veya "0.13 kg/m²"
    m = text.match(/([\d.,]+)\s*[-–—]?\s*([\d.,]+)?\s*(lt|litre|l|kg)\s*\/\s*m²/i);
    if (m) {
        const n = [m[1], m[2]].filter(Boolean).map((x) => parseFloat(x.replace(',', '.'))).filter((v) => v > 0);
        if (n.length) return { mode: 'per_area', unit: /kg/i.test(m[3]) ? 'kg' : 'L', min: Math.min(...n), max: Math.max(...n), label: m[0].trim() };
    }
    return null;
}

export default function ConsumptionCalculator({ product, isDark, primaryColor }) {
    const rate = useMemo(() => parseConsumptionRate(product), [product]);
    const [area, setArea] = useState('');
    const [coats, setCoats] = useState(1);

    if (!rate) return null; // sarfiyat verisi çözülemiyorsa hesaplayıcıyı gösterme

    const A = parseFloat(String(area).replace(',', '.'));
    const valid = !isNaN(A) && A > 0;
    let rMin = null, rMax = null;
    if (valid) {
        if (rate.mode === 'per_area') {
            rMin = A * rate.min * coats;
            rMax = A * rate.max * coats;
        } else { // coverage: yüksek kapsama = az boya
            rMin = (A / rate.max) * coats;
            rMax = (A / rate.min) * coats;
        }
    }
    const fmt = (n) => (Math.round(n * 100) / 100).toLocaleString('tr-TR');

    return (
        <section className={`p-8 rounded-[2.5rem] border backdrop-blur-2xl pointer-events-auto ${isDark ? 'bg-white/5 border-white/10' : 'bg-white/40 border-black/5'}`}>
            <h3 className="text-lg font-bold uppercase tracking-widest flex items-center gap-2 mb-2" style={{ color: primaryColor }}>
                <Calculator size={18} /> SARFİYAT HESAPLAMA
            </h3>
            <p className="text-[11px] font-medium opacity-50 mb-6">Sarfiyat esası: {rate.label}</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                    <label className="text-[10px] font-black uppercase tracking-widest opacity-50">Boyanacak Alan (m²)</label>
                    <input
                        type="number" min="0" inputMode="decimal" value={area}
                        onChange={(e) => setArea(e.target.value)} placeholder="Örn. 40"
                        className={`w-full mt-2 px-4 py-3 rounded-xl border outline-none font-bold focus:ring-2 transition-all ${isDark ? 'bg-black/20 border-white/10 text-white placeholder:text-white/20' : 'bg-white/60 border-black/10 text-slate-900 placeholder:text-slate-300'}`}
                        style={{ '--tw-ring-color': primaryColor }}
                    />
                </div>
                <div>
                    <label className="text-[10px] font-black uppercase tracking-widest opacity-50">Kat Sayısı</label>
                    <div className="flex gap-2 mt-2">
                        {[1, 2, 3].map((c) => (
                            <button
                                key={c} type="button" onClick={() => setCoats(c)}
                                className={`flex-1 py-3 rounded-xl font-black text-sm border transition-all active:scale-95 ${coats === c ? 'text-white shadow-lg' : (isDark ? 'border-white/10 text-white/50 hover:text-white/80' : 'border-black/10 text-slate-500 hover:text-slate-800')}`}
                                style={coats === c ? { backgroundColor: primaryColor, borderColor: 'transparent' } : {}}
                            >
                                {c}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            <div className={`mt-6 p-5 rounded-2xl text-center ${isDark ? 'bg-white/5' : 'bg-black/5'}`}>
                <p className="text-[10px] font-black uppercase tracking-widest opacity-40 mb-1">Yaklaşık Gereken Miktar</p>
                {valid ? (
                    <p className="text-3xl md:text-4xl font-black" style={{ color: primaryColor }}>
                        {Math.abs(rMin - rMax) < 0.01 ? `${fmt(rMin)} ${rate.unit}` : `${fmt(rMin)} – ${fmt(rMax)} ${rate.unit}`}
                    </p>
                ) : (
                    <p className="text-sm font-bold opacity-40">Alanı girin, miktar otomatik hesaplansın</p>
                )}
            </div>
            <p className="text-[9px] opacity-30 mt-3 text-center leading-relaxed">
                * Yüzey emiciliği ve uygulama koşullarına göre değişebilir. Kesin sarfiyat için saha numune çalışması önerilir.
            </p>
        </section>
    );
}
